import type { BillToInfo, PqPaymentInfo, ProblemDetails } from './pq-client';

export type PaymentEnvironment = 'sandbox' | 'production';

export function paymentEnvironment(value: string | undefined, development: boolean): PaymentEnvironment | null {
    if (value === 'sandbox' || value === 'production') return value;
    return !value && development ? 'sandbox' : null;
}

/** U.S. states, D.C., territories and military postal codes: the states the billing API accepts. */
export const US_STATES = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
    { value: 'DC', label: 'District of Columbia' },
    { value: 'AS', label: 'American Samoa' },
    { value: 'GU', label: 'Guam' },
    { value: 'MP', label: 'Northern Mariana Islands' },
    { value: 'PR', label: 'Puerto Rico' },
    { value: 'VI', label: 'U.S. Virgin Islands' },
    { value: 'AA', label: 'Armed Forces Americas (AA)' },
    { value: 'AE', label: 'Armed Forces Europe (AE)' },
    { value: 'AP', label: 'Armed Forces Pacific (AP)' },
];
const STATE_CODES = new Set(US_STATES.map((state) => state.value));

export const EMPTY_BILL_TO: BillToInfo = { firstName: '', lastName: '', company: '', address: '', city: '', state: '', zip: '', country: 'USA' };

/** Fills missing fields from the API with form defaults, so every input is controlled by a string. */
export const normalizeBillTo = (billTo: BillToInfo | null): BillToInfo => Object.fromEntries(
    Object.entries(EMPTY_BILL_TO).map(([key, fallback]) => [key, billTo?.[key as keyof BillToInfo] ?? fallback]),
) as BillToInfo;

/**
 * Trims every field and fixes the country to USA (the only one the API supports). The form submits
 * these values, so validation and the API see the same text.
 */
export const trimBillTo = (billTo: BillToInfo): BillToInfo => ({
    ...Object.fromEntries(Object.entries(billTo).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])) as BillToInfo,
    country: 'USA',
});

type Validator = (value: string | null, values?: BillToInfo) => string | null;

/** The API also limits "first last" to 100 characters. */
const MAX_FULL_NAME = 100;
const fullNameTooLong = (values?: BillToInfo) =>
    `${values?.firstName?.trim() ?? ''} ${values?.lastName?.trim() ?? ''}`.length > MAX_FULL_NAME;

const requiredMax = (label: string, max: number): Validator => (value) => {
    const text = value?.trim() ?? '';
    if (!text) return `${label} is required`;
    return text.length > max ? `${label} must be ${max} characters or fewer` : null;
};

export const validateCompany: Validator = (value) =>
    (value?.trim().length ?? 0) > 50 ? 'Company must be 50 characters or fewer' : null;

const ZIP_PATTERN = /^\d{5}(-\d{4})?$/;

/** Bill-to field rules, in the shape Mantine's useForm `validate` expects. */
export const billToValidators: Partial<Record<keyof BillToInfo, Validator>> = {
    firstName: requiredMax('First name', 50),
    lastName: (value, values) => requiredMax('Last name', 50)(value)
        ?? (fullNameTooLong(values) ? `First and last name together must be ${MAX_FULL_NAME - 1} characters or fewer` : null),
    company: validateCompany,
    address: requiredMax('Address', 50),
    city: requiredMax('City', 30),
    state: (value) => {
        const code = value?.trim().toUpperCase() ?? '';
        if (!code) return 'State is required';
        return STATE_CODES.has(code) ? null : 'Choose a U.S. state, territory or military code';
    },
    zip: (value) => {
        const text = value?.trim() ?? '';
        if (!text) return 'ZIP is required';
        return ZIP_PATTERN.test(text) ? null : 'Enter a 5-digit ZIP or ZIP+4 (12345-6789)';
    },
};

/** Every required bill-to field has a value, so the saved address can be sent with a new card as-is. */
export const isBillToComplete = (billTo: BillToInfo | null): boolean =>
    !!billTo && (['firstName', 'lastName', 'address', 'city', 'state', 'zip'] as const).every((field) => billTo[field]?.trim());

/**
 * Parses Authorize.Net's card expiry ("MM/YY", also "MMYY" or "MM/YYYY") into month and 4-digit year.
 * Returns nulls for anything else, so an unexpected format hides the expiry instead of showing a wrong one.
 */
export function parseCardExpiry(value: string | null | undefined): { month: number | null; year: number | null } {
    const match = /^\s*(\d{1,2})\s*\/?\s*(\d{2}|\d{4})\s*$/.exec(value ?? '');
    const month = match ? Number(match[1]) : NaN;
    if (!match || month < 1 || month > 12) return { month: null, year: null };
    const year = Number(match[2]);
    return { month, year: match[2].length === 2 ? 2000 + year : year };
}

/** One-line card summary, e.g. "VISA ending in 4242, expires 08/28". */
export function describeCard(card: Pick<PqPaymentInfo, 'cardType' | 'cardLast4' | 'expiryMonth' | 'expiryYear'>): string {
    const name = card.cardType?.trim() || 'Card';
    const ending = card.cardLast4 ? ` ending in ${card.cardLast4}` : '';
    const expires = card.expiryMonth && card.expiryYear
        ? `, expires ${String(card.expiryMonth).padStart(2, '0')}/${String(card.expiryYear).slice(-2)}`
        : '';
    return `${name}${ending}${expires}`;
}

/** Address lines for display; empty when nothing is saved. */
export function describeBillTo(billTo: BillToInfo | null): string[] {
    if (!billTo) return [];
    const name = [billTo.firstName, billTo.lastName].filter(Boolean).join(' ');
    const cityLine = [billTo.city, [billTo.state, billTo.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    return [name, billTo.company, billTo.address, cityLine].map((line) => line?.trim() ?? '').filter(Boolean);
}

export type BillingSave = 'payment-method' | 'billing-information';

/** What the billing screen should do after a failed save. */
export interface BillingFailure {
    message: string;
    next: 'retry' | 'new-card' | 'fix-fields' | 'sign-in' | 'reload' | 'support';
    fieldErrors?: Partial<Record<keyof BillToInfo, string>>;
}

const PROBLEM = 'urn:pquser:errors:';

/** Maps ASP.NET validation keys like "BillTo.FirstName" onto form fields. */
function billToFieldErrors(problem: ProblemDetails | null): Partial<Record<keyof BillToInfo, string>> {
    const errors = problem?.errors;
    if (!errors || typeof errors !== 'object') return {};
    const fields = new Set(Object.keys(EMPTY_BILL_TO));
    const result: Partial<Record<keyof BillToInfo, string>> = {};
    for (const [key, messages] of Object.entries(errors as Record<string, unknown>)) {
        const last = key.split('.').pop() ?? '';
        const field = last.charAt(0).toLowerCase() + last.slice(1);
        const message = Array.isArray(messages) ? messages.find((entry) => typeof entry === 'string') : undefined;
        if (fields.has(field) && message) result[field as keyof BillToInfo] = message;
    }
    return result;
}

/**
 * Turns a failed billing save into a message and next step, per the PQUser billing contract.
 * status 0 means no response (network failure or timeout), so the outcome is unknown.
 * Card nonces are single-use: any failed card save needs the card entered again.
 */
export function describeBillingFailure(save: BillingSave, status: number, problem: ProblemDetails | null): BillingFailure {
    const card = save === 'payment-method';
    const detail = typeof problem?.detail === 'string' && problem.detail.trim() ? problem.detail.trim() : null;
    const type = problem?.type ?? '';
    if (status === 401) return { message: 'Your session has expired. Sign in again to continue.', next: 'sign-in' };
    if (status === 403) return { message: "Billing for this account can't be managed in the app. Please contact ParcelQuest support.", next: 'support' };
    if (type === `${PROBLEM}payment-method-declined` || status === 402) {
        return { message: `Your card was declined${detail ? `: ${detail}` : '.'} Please try another card.`, next: 'new-card' };
    }
    if (type === `${PROBLEM}payment-method-rejected`) {
        return { message: detail ?? 'The payment processor rejected this update.', next: card ? 'new-card' : 'retry' };
    }
    if (type === `${PROBLEM}account-pending-signup`) {
        return { message: "Your account signup isn't finished yet. Complete signup before updating billing.", next: 'support' };
    }
    if (type === `${PROBLEM}billing-profile-unresolved`) {
        return { message: "We couldn't find the card on file for this account. Please contact ParcelQuest support.", next: 'support' };
    }
    if (type === `${PROBLEM}payment-method-update-in-progress`) {
        return { message: 'Another billing update was in progress. Your saved details have been reloaded; check them before saving again.', next: 'reload' };
    }
    if (type === `${PROBLEM}payment-processor-unavailable` || status === 502) {
        return { message: 'The payment service is temporarily unavailable. Please try again in a few minutes.', next: card ? 'new-card' : 'retry' };
    }
    if (status === 400) {
        const fieldErrors = billToFieldErrors(problem);
        if (Object.keys(fieldErrors).length > 0) return { message: 'Please correct the highlighted billing details.', next: 'fix-fields', fieldErrors };
        return { message: detail ?? 'Some billing details were not accepted. Please check them and try again.', next: card ? 'new-card' : 'retry' };
    }
    if (status === 0 || status >= 500) {
        // The provider and databases aren't updated atomically, so a failure here may be partial.
        return card
            ? { message: "We couldn't confirm whether your card was saved. Refresh to check the card on file before trying again.", next: 'reload' }
            : { message: "We couldn't confirm your billing address was saved. Your details are still here; please try saving again.", next: 'retry' };
    }
    return { message: detail ?? 'Something went wrong. Please try again.', next: card ? 'new-card' : 'retry' };
}
